import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'archive' | 'unarchive';
  articleTitle: string;
  busy?: boolean;
  onConfirm: () => Promise<void> | void;
}

/**
 * ArchiveArticleDialog — mirrors ArchiveCompanyDialog (Plan 02-09).
 *
 * Plan 02-12 pins the archive title copy as "Archive {title}?" — do not
 * paraphrase. Unarchive mode uses a neutral copy with no warning text since
 * unarchiving is reversible and non-destructive.
 *
 * NOTE: the plan's files_modified initially named this ArticleFormDialog,
 * but the Task 1 action block explicitly directed renaming to
 * ArchiveArticleDialog for clarity. The file name matches the component's
 * actual responsibility (archive-confirm, not article-form).
 */
export function ArchiveArticleDialog({
  open,
  onOpenChange,
  mode,
  articleTitle,
  busy,
  onConfirm,
}: Props) {
  const isArchive = mode === 'archive';
  const title = isArchive ? `Archive ${articleTitle}?` : `Unarchive ${articleTitle}?`;
  const description = isArchive
    ? `Archive ${articleTitle}? The article will be hidden from the default list but historical references are preserved.`
    : `Unarchive ${articleTitle}? The article will be visible in the active list again.`;
  const confirmLabel = isArchive ? 'Archive' : 'Unarchive';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={isArchive ? 'destructive' : 'default'}
            disabled={busy}
            onClick={() => void onConfirm()}
          >
            {busy ? 'Saving…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
